import React, { useState, useEffect } from 'react';
import { FiUsers, FiMail, FiDownload, FiSearch, FiTrash2, FiAlertCircle, FiSend, FiEye, FiCalendar, FiRefreshCw } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

// Get admin path from environment variables or use the default
const ADMIN_ACCESS_PATH = process.env.REACT_APP_ADMIN_PATH || 'secure-admin-route';

// Define type for subscriber data
interface Subscriber {
  id: string;
  email: string;
  createdAt: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
}

interface SubscribersResponse {
  subscribers: Subscriber[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const SubscribersFullView: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState<string[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalSubscribers, setTotalSubscribers] = useState(0);

  // Delete operation state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subscriberToDelete, setSubscriberToDelete] = useState<Subscriber | null>(null);
  
  // Viewing subscriber details
  const [viewingSubscriber, setViewingSubscriber] = useState<Subscriber | null>(null);

  // Fetch subscribers data from API
  useEffect(() => {
    fetchSubscribers();
  }, [currentPage, itemsPerPage]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        throw new Error('Authentication token missing. Please log in again.');
      }
      
      const response = await fetch(`/api/admin/subscribers?page=${currentPage}&limit=${itemsPerPage}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
        } catch (e) {
          errorMessage = `API error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      const data: SubscribersResponse = await response.json();
      setSubscribers(data.subscribers || []);
      setTotalSubscribers(data.total || 0);
      setError(null);
      setIsInitialLoad(false);
      
      // Clear selections when page changes
      setSelectedSubscribers([]);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(err instanceof Error ? err.message : 'Could not load subscriber data');
    } finally {
      setLoading(false);
    }
  };

  // Show success message with auto-dismiss
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  // Export subscribers to CSV
  const exportToCsv = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`/api/admin/subscribers/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `subscribers_export_${new Date().toISOString().slice(0,10)}.csv`);
      a.click();
      URL.revokeObjectURL(url);
      
      showSuccess('Subscribers exported successfully!');
    } catch (err) {
      console.error('Error exporting subscribers:', err);
      setError('Failed to export subscribers');
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return dateStr;
    }
  };

  // Filter subscribers based on search term
  const filteredSubscribers = searchTerm
    ? subscribers.filter(sub => 
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.utmSource && sub.utmSource.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (sub.utmMedium && sub.utmMedium.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (sub.utmCampaign && sub.utmCampaign.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : subscribers;

  // Pagination calculations
  const totalPages = Math.ceil(totalSubscribers / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalSubscribers);

  // Pagination function
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Handle subscriber selection
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedSubscribers([]);
  };

  const toggleSubscriberSelection = (subscriberId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSubscribers(prev => {
      if (prev.includes(subscriberId)) {
        return prev.filter(id => id !== subscriberId);
      } else {
        return [...prev, subscriberId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedSubscribers.length === filteredSubscribers.length && filteredSubscribers.length > 0) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(filteredSubscribers.map(sub => sub.id));
    }
  };

  // Handle sending newsletter to selected subscribers
  const handleSendNewsletter = () => {
    if (selectedSubscribers.length === 0) {
      setError('Please select at least one subscriber');
      return;
    }
    
    navigate(`/${ADMIN_ACCESS_PATH}/newsletter`, { 
      state: { 
        selectedSubscriberIds: selectedSubscribers,
        activeTab: 'newsletter'
      } 
    });
  };

  // View subscriber details
  const handleViewSubscriber = (subscriber: Subscriber) => {
    setViewingSubscriber(subscriber);
  };

  const closeViewModal = () => {
    setViewingSubscriber(null);
  };

  // Delete operations
  const handleDeleteClick = (subscriber: Subscriber) => {
    setSubscriberToDelete(subscriber);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!subscriberToDelete) return;
    
    try {
      setDeletingId(subscriberToDelete.id);
      
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        throw new Error('Authentication token missing. Please log in again.');
      }
      
      const response = await fetch(`/api/admin/subscribers/${subscriberToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      let responseData;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        throw new Error(`API returned invalid response (Status: ${response.status})`);
      }
      
      if (!response.ok) {
        const errorMessage = responseData?.message || responseData?.error || `API error: ${response.status}`;
        throw new Error(errorMessage);
      }
      
      // Success - remove from local state and refresh
      setTotalSubscribers(prev => prev - 1);
      setShowDeleteConfirm(false);
      setSubscriberToDelete(null);
      showSuccess('Subscriber deleted successfully!');
      
      // Refresh the current page
      fetchSubscribers();
      
    } catch (err) {
      console.error('Error deleting subscriber:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete subscriber');
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSubscriberToDelete(null);
  };

  // Loading overlay component
  const LoadingOverlay = () => loading && !isInitialLoad ? (
    <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10 rounded-lg">
      <div className="flex items-center space-x-2 text-primary">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
        <span>Loading...</span>
      </div>
    </div>
  ) : null;

  return (
    <div className="bg-background p-6 rounded-lg shadow-md relative">
      <LoadingOverlay />
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-primary">Subscribers ({totalSubscribers})</h2>
        
        <div className="flex space-x-2">
          <button
            onClick={fetchSubscribers}
            className="px-3 py-1 rounded-md text-sm bg-background-light text-primary hover:bg-gray-800 flex items-center"
            title="Refresh"
          >
            <FiRefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={exportToCsv}
            className="px-3 py-1 rounded-md text-sm bg-background-light text-primary hover:bg-gray-800 flex items-center"
          >
            <FiDownload className="w-4 h-4 mr-1" />
            Export
          </button>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search subscribers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-background-light border border-gray-800 rounded-lg py-2 pl-10 pr-4 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <label htmlFor="itemsPerPage" className="text-sm text-secondary">Show:</label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-background-light border border-gray-800 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-secondary">per page</span>
          </div>
        </div>
      </div>

      {/* Action buttons for bulk operations */}
      {filteredSubscribers.length > 0 && (
        <div className="flex mb-4 space-x-2">
          <button 
            onClick={toggleSelectMode}
            className={`px-3 py-1 rounded-md text-sm ${
              selectMode 
                ? 'bg-accent text-white' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            {selectMode ? 'Cancel Selection' : 'Select Multiple'}
          </button>
          
          {selectMode && (
            <>
              <button 
                onClick={toggleSelectAll}
                className="px-3 py-1 rounded-md text-sm bg-background-light text-primary hover:bg-gray-800"
              >
                {selectedSubscribers.length === filteredSubscribers.length && filteredSubscribers.length > 0 ? 'Deselect All' : 'Select All'}
              </button>
              
              <button 
                onClick={handleSendNewsletter}
                className={`px-3 py-1 rounded-md text-sm flex items-center ${
                  selectedSubscribers.length > 0
                    ? 'bg-accent text-white hover:bg-accent/90'
                    : 'bg-accent/50 text-white/50 cursor-not-allowed'
                }`}
                disabled={selectedSubscribers.length === 0}
              >
                <FiSend className="mr-1 h-3 w-3" />
                Send Newsletter ({selectedSubscribers.length})
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/50 text-green-400 px-4 py-3 rounded-md mb-6 animate-fade-in">
          {successMessage}
        </div>
      )}

      {filteredSubscribers.length === 0 ? (
        <div className="text-center py-12 text-secondary">
          <FiUsers className="mx-auto h-12 w-12 mb-4 opacity-30" />
          <p>No subscribers found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-6 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <div className="inline-block min-w-full align-middle px-6">
            <div className="overflow-hidden border border-gray-800 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-background-light">
                  <tr>
                    {selectMode && (
                      <th className="pl-4 pr-2 py-3 sticky left-0 bg-background-light z-10">
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 text-accent rounded border-gray-700 focus:ring-accent focus:ring-opacity-25 bg-background"
                          checked={selectedSubscribers.length === filteredSubscribers.length && filteredSubscribers.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Signed Up</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider sticky right-0 bg-background-light z-10">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-gray-800">
                  {filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id} className={selectMode && selectedSubscribers.includes(subscriber.id) ? "bg-accent/10" : ""}>
                      {selectMode && (
                        <td className="pl-4 pr-2 py-4 whitespace-nowrap sticky left-0 bg-background z-10">
                                                     <input 
                             type="checkbox" 
                             className="w-4 h-4 text-accent rounded border-gray-700 focus:ring-accent focus:ring-opacity-25 bg-background"
                             checked={selectedSubscribers.includes(subscriber.id)}
                             onChange={(e) => toggleSubscriberSelection(subscriber.id, e as unknown as React.MouseEvent)}
                             onClick={(e) => e.stopPropagation()}
                           />
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className={`flex items-center ${selectMode ? "" : "cursor-pointer hover:bg-background-light p-1 rounded-md transition-colors"}`}
                          onClick={selectMode ? (e) => toggleSubscriberSelection(subscriber.id, e) : () => handleViewSubscriber(subscriber)}
                        >
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-accent/20 flex items-center justify-center mr-3">
                              <FiMail className="h-4 w-4 text-accent" />
                            </div>
                            <div className="text-sm font-medium text-primary">{subscriber.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        <div className="flex items-center">
                          <FiCalendar className="h-4 w-4 mr-2" />
                          {formatDate(subscriber.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {subscriber.utmSource ? (
                          <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
                            {subscriber.utmSource}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {subscriber.utmCampaign ? (
                          <span className="px-2 py-1 bg-background-light text-primary text-xs rounded-full">
                            {subscriber.utmCampaign}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap sticky right-0 bg-background z-10">
                        <div className="flex space-x-2">
                          <button 
                            className="text-blue-400 hover:text-blue-300"
                            title="View details"
                            onClick={() => handleViewSubscriber(subscriber)}
                          >
                            <FiEye />
                          </button>
                          <button 
                            className="text-red-400 hover:text-red-300"
                            title="Delete subscriber"
                            onClick={() => handleDeleteClick(subscriber)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination UI */}
      <div className="flex justify-between items-center mt-4">
        <div className="text-sm text-gray-400">
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalSubscribers)} of {totalSubscribers} subscribers
        </div>
        
        <div className="flex space-x-1">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === 1 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            Previous
          </button>
          
          {Array.from({ length: Math.ceil(totalSubscribers / itemsPerPage) }).map((_, index) => (
            <button
              key={index}
              onClick={() => paginate(index + 1)}
              className={`px-3 py-1 rounded-md text-sm ${
                currentPage === index + 1 
                  ? 'bg-accent text-white' 
                  : 'bg-background-light text-primary hover:bg-gray-800'
              }`}
            >
              {index + 1}
            </button>
          )).slice(
            Math.max(0, currentPage - 3),
            Math.min(Math.ceil(totalSubscribers / itemsPerPage), currentPage + 2)
          )}
          
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(totalSubscribers / itemsPerPage)}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === Math.ceil(totalSubscribers / itemsPerPage) 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* View Subscriber Modal */}
      {viewingSubscriber && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-background border border-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-800">
              <h3 className="text-xl font-semibold text-primary">Subscriber Details</h3>
              <button onClick={closeViewModal} className="text-gray-400 hover:text-gray-300">
                ×
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-accent">Email</label>
                  <p className="text-primary">{viewingSubscriber.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-accent">Signed Up</label>
                  <p className="text-primary">{formatDate(viewingSubscriber.createdAt)}</p>
                </div>
                {viewingSubscriber.utmSource && (
                  <div>
                    <label className="text-sm font-medium text-accent">UTM Source</label>
                    <p className="text-primary">{viewingSubscriber.utmSource}</p>
                  </div>
                )}
                {viewingSubscriber.utmMedium && (
                  <div>
                    <label className="text-sm font-medium text-accent">UTM Medium</label>
                    <p className="text-primary">{viewingSubscriber.utmMedium}</p>
                  </div>
                )}
                {viewingSubscriber.utmCampaign && (
                  <div>
                    <label className="text-sm font-medium text-accent">UTM Campaign</label>
                    <p className="text-primary">{viewingSubscriber.utmCampaign}</p>
                  </div>
                )}
                {viewingSubscriber.referrer && (
                  <div>
                    <label className="text-sm font-medium text-accent">Referrer</label>
                    <p className="text-primary break-all">{viewingSubscriber.referrer}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && subscriberToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-background-light rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <FiAlertCircle className="text-red-500 mr-2" />
              Delete Subscriber
            </h3>
            <p className="mb-6">
              Are you sure you want to delete <strong>{subscriberToDelete.email}</strong>? This action cannot be undone.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deletingId !== null}
                className="px-4 py-2 border border-gray-700 rounded hover:bg-background"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingId !== null}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-white flex items-center gap-2"
              >
                {deletingId !== null ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <FiTrash2 size={16} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribersFullView; 